import { useState } from "react";
import { ShieldCheck, Download } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { FilterChips, SearchInput, Button, Avatar, EmptyState, Tooltip } from "@/design/components";
import { formatDateTime, formatHumanCurrency } from "@/design/format";
import { usePeopleIndex, useOrgIndex } from "@/domain/hooks";
import type { AuditEvent } from "@/domain/types";

const CLASSES = [
  { value: "all", label: "All" },
  { value: "approval", label: "Approval" },
  { value: "spend", label: "Spend" },
  { value: "evidence", label: "Evidence" },
  { value: "onboarding", label: "Onboarding" },
  { value: "kyc", label: "KYC" },
  { value: "status_change", label: "Status change" },
];

export function AuditTrail({ events, readonly }: { events: AuditEvent[]; readonly?: boolean }) {
  const people = usePeopleIndex();
  const orgs = useOrgIndex();
  const [cls, setCls] = useState("all");
  const [q, setQ] = useState("");

  // Seed lookup first, then the name the backend resolved, then the raw id.
  // Demo events reference seed people and resolve through the index; live
  // ledger events carry a real numeric actor id that isn't in it, and would
  // otherwise render as a bare number.
  const actorName = (e: AuditEvent) => people[e.actorId]?.fullName ?? e.actorName ?? e.actorId;
  const orgName = (e: AuditEvent) =>
    orgs[e.actorOrganisationId]?.name ?? e.actorOrganisationName ?? "";
  const actorInitials = (e: AuditEvent) =>
    people[e.actorId]?.avatarInitials ?? initialsFrom(e.actorName) ?? "?";

  const rows = events.filter((e) => {
    if (cls !== "all" && e.class !== cls) return false;
    if (q && !e.description.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  function handleExport() {
    if (rows.length === 0) {
      toast.error("No events to export for the current filters.");
      return;
    }
    const header = ["Timestamp", "Class", "Actor", "Organisation", "Amount", "Description", "Reference"];
    const body = rows.map((e) => [
      e.timestamp,
      classLabel(e.class),
      actorName(e),
      orgName(e),
      e.amount != null ? String(e.amount) : "",
      e.description,
      e.refId ?? "",
    ]);
    const csv = [header, ...body].map((r) => r.map(csvCell).join(",")).join("\r\n");
    const filename = `audit-trail-${new Date().toISOString().slice(0, 10)}.csv`;
    downloadCsv(csv, filename);
    toast.success(`Exported ${rows.length} event${rows.length === 1 ? "" : "s"} as ${filename}`);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between rounded-md border border-line bg-subtle/60 px-4 py-2.5">
        <span className="flex items-center gap-2 text-xs text-mute">
          <ShieldCheck className="h-3.5 w-3.5 text-faint" />
          Tamper-evident activity log. Every entry is sealed at the time of the action and cannot be edited after the fact.
        </span>
        <span className="text-xs text-faint">{events.length} recorded events</span>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <FilterChips options={CLASSES} value={cls} onChange={setCls} />
        <div className="flex items-center gap-2">
          <SearchInput value={q} onChange={setQ} placeholder="Search events" className="w-56" />
          {!readonly && (
            <Button variant="secondary" size="sm" onClick={handleExport}>
              <Download className="h-4 w-4" /> Export (CSV)
            </Button>
          )}
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState title="No matching events" body="Adjust the filters to see audit events. Every approval, spend, evidence and onboarding action is recorded here." />
      ) : (
        <ol className="overflow-hidden rounded-lg border border-line bg-card">
          {rows.map((e, i) => (
            <li key={e.id} className="flex items-start gap-3 border-b border-hairline px-4 py-3 last:border-0">
              <span className="relative mt-0.5">
                {i < rows.length - 1 && <span className="absolute left-1/2 top-7 h-full w-px -translate-x-1/2 bg-hairline" aria-hidden />}
                <Avatar initials={actorInitials(e)} size={24} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-ink">{e.description}</p>
                <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-faint">
                  {e.amount != null && (
                    <span className="rounded bg-subtle px-1.5 py-0.5 font-medium tabular-nums text-mute">{formatHumanCurrency(e.amount)}</span>
                  )}
                  <span>
                    {actorName(e)}
                    {orgName(e) ? ` · ${orgName(e)}` : ""} · {classLabel(e.class)}
                  </span>
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Tooltip label={formatDateTime(e.timestamp)}>
                  <span className="text-xs text-faint">{formatDistanceToNow(new Date(e.timestamp), { addSuffix: true })}</span>
                </Tooltip>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/** "Amina Nakimuli" -> "AN". Undefined when there's no name to derive from. */
function initialsFrom(fullName?: string): string | undefined {
  if (!fullName) return undefined;
  const letters = fullName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("");
  return letters ? letters.toUpperCase() : undefined;
}

function classLabel(c: AuditEvent["class"]): string {
  return { approval: "Approval", spend: "Spend", evidence: "Evidence", onboarding: "Onboarding", kyc: "KYC", status_change: "Status change", system: "System" }[c];
}

/** Quote a single CSV field, escaping embedded quotes and wrapping when it contains a delimiter. */
function csvCell(value: string): string {
  const needsQuoting = /[",\r\n]/.test(value);
  const escaped = value.replace(/"/g, '""');
  return needsQuoting ? `"${escaped}"` : escaped;
}

function downloadCsv(csv: string, filename: string): void {
  // BOM so Excel opens UTF-8 content (e.g. currency, accented names) correctly.
  const blob = new Blob(["﻿", csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
